/**
 * The delivery state of a user's send (mission stream contract phase 4,
 * challenge finding M7, agion/Agion#2327).
 *
 * The states and what each means are one Go table
 * (platform/shared/contract/missionstream/missionstream_delivery_state.go),
 * generated into generated/missionstream.ts as DeliveryState and
 * DeliveryStates. This module holds the rules every app follows for them:
 *
 * - nextDeliveryState(): how a send's state changes when something happens to
 *   it (it leaves the browser, a receipt or a refusal arrives, time runs out);
 * - awaitsReceipt(): whether a send still waits for the relay, so its timer
 *   runs;
 * - returnsToComposer(): whether a send goes back to the composer with its
 *   text, its files and its question;
 * - receiptEvent(): the event a receipt of a message sent while the agent
 *   works is, by its outcome.
 *
 * Phase 4 needs no wire change: the relay's echo of an answer
 * (mission.hitl.response) is its receipt, and a delivery_failed error for its
 * request refuses it. Phase 6 adds queued: a message sent while the agent
 * works that the run accepted (receipt accepted) and that waits for the
 * agent's next turn (receipt delivered), or that the run ended before
 * (receipt undelivered). Every switch here and in the apps ends in
 * unknownKind(), so a state added in Go fails `tsc` until each rule decides
 * it (deliveryState.test.ts proves it).
 */

import { type DeliveryState, type ReceiptOutcome } from './generated/missionstream'
import { unknownKind } from './messagePlacement'

/**
 * How long a browser waits for the relay's receipt of an answer before the
 * answer counts as failed and goes back to the composer: 20 seconds, the
 * relay's own pongWait.
 *
 * The relay decides an answer in the read loop that received the frame
 * (streaming-service internal/relay/client.go handleHITLResponse): it refuses
 * it with delivery_failed, or routes it to the host and echoes
 * mission.hitl.response to the run's subscribers
 * (status.go RouteHITLResponseToHost). Nothing on that path waits for the
 * host; its only waits are bounded Redis calls for a run on another pod and
 * writeWait (10 s) to write the receipt to the browser. pongWait (20 s,
 * client.go) is how long the relay itself keeps a silent browser connection,
 * so a receipt that has not come by then was lost with its frame or its
 * connection. A receipt that still arrives later turns the answer delivered.
 */
export const ANSWER_RECEIPT_TIMEOUT_MS = 20000

/** Something that happened to a send. */
export type DeliveryEvent =
  /** The send left the browser: an answer frame went out, or a launch request. */
  | 'sent'
  /** The browser could not send it: the connection was down, or the launch request failed. */
  | 'not_sent'
  /** Its receipt arrived: the relay's echo of the answer, or the launched run. */
  | 'receipt'
  /** The relay refused it (delivery_failed for its request, or a refused receipt). */
  | 'refusal'
  /** ANSWER_RECEIPT_TIMEOUT_MS passed without a receipt or a refusal. */
  | 'timeout'
  /**
   * The host answered that it cannot confirm the answer (a question answer
   * result with outcome unknown, agion/Agion#2767). Like a timeout, the send
   * fails and goes back to the composer; a later receipt still delivers it.
   */
  | 'unconfirmed'
  /** The run accepted a message sent while the agent works (receipt accepted). */
  | 'accepted'
  /** The run ended before the agent got a queued message (receipt undelivered). */
  | 'undelivered'

/**
 * A send's state after an event. A receipt is the only way to delivered,
 * also after a timeout, because a late receipt proves the send arrived; a
 * late accepted receipt likewise makes a failed send queued. A refusal after
 * a timeout replaces it, because it carries the relay's reason. A queued
 * send has no timer: the agent takes it at its next turn, however long the
 * current one runs, and only the run ending first fails it. Nothing moves a
 * send out of delivered or refused.
 */
export function nextDeliveryState(state: DeliveryState, event: DeliveryEvent): DeliveryState {
  switch (state) {
    case 'waiting':
      return event === 'sent' ? 'sending' : event === 'not_sent' ? 'failed' : state
    case 'sending':
      return afterSending(event)
    case 'queued':
      return event === 'receipt' ? 'delivered' : event === 'refusal' ? 'refused' : event === 'undelivered' ? 'failed' : state
    case 'failed':
      return event === 'receipt' ? 'delivered' : event === 'refusal' ? 'refused' : event === 'accepted' ? 'queued' : state
    case 'delivered':
    case 'refused':
      return state
    default:
      return unknownKind(state, state)
  }
}

function afterSending(event: DeliveryEvent): DeliveryState {
  switch (event) {
    case 'sent':
      return 'sending'
    case 'accepted':
      return 'queued'
    case 'receipt':
      return 'delivered'
    case 'refusal':
      return 'refused'
    case 'not_sent':
    case 'timeout':
    case 'unconfirmed':
    case 'undelivered':
      return 'failed'
    default:
      return unknownKind(event, 'sending')
  }
}

/**
 * The event a receipt of a message sent while the agent works is, by its
 * outcome (the generated ReceiptOutcome table): accepted queues the message,
 * delivered is its receipt, refused is a refusal and undelivered fails it.
 * nextDeliveryState() then keeps the receipts monotonic: a receipt that comes
 * late or twice never moves a message back. An outcome this build does not
 * know is no event (null), so the message keeps its state.
 */
export function receiptEvent(outcome: ReceiptOutcome): DeliveryEvent | null {
  switch (outcome) {
    case 'accepted':
      return 'accepted'
    case 'delivered':
      return 'receipt'
    case 'refused':
      return 'refusal'
    case 'undelivered':
      return 'undelivered'
    default:
      return unknownKind(outcome, null)
  }
}

/**
 * Whether a send in this state waits for the relay's receipt, so its timer
 * runs. A queued send's run has it, so its timer stops.
 */
export function awaitsReceipt(state: DeliveryState): boolean {
  switch (state) {
    case 'sending':
      return true
    case 'waiting':
    case 'queued':
    case 'delivered':
    case 'failed':
    case 'refused':
      return false
    default:
      return unknownKind(state, false)
  }
}

/**
 * Whether a send in this state goes back to the composer with its text, its
 * files and its question, so the user can send it again.
 */
export function returnsToComposer(state: DeliveryState): boolean {
  switch (state) {
    case 'failed':
    case 'refused':
      return true
    case 'waiting':
    case 'sending':
    case 'queued':
    case 'delivered':
      return false
    default:
      return unknownKind(state, false)
  }
}
